/**
 * Edit-lock service — soft, advisory presence locks so only one user edits an
 * event at a time. Wraps the backend lock endpoints (POST/PUT/DELETE/GET
 * /events/{id}/lock and GET /locks) via the shared authenticated client.
 *
 * A single page-lifetime session id distinguishes this browser tab from the
 * same user's other tabs, matching the backend's `session_id` semantics.
 */
import { authedFetch } from './apiClient';
import { getCurrentUserAlias } from '../auth';
import { createLogger } from '../utils/logger';

const logger = createLogger('LockService');

/** Per-tab session id used to scope lock ownership (survives for the page life). */
const SESSION_ID: string =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * Supported stored event-id formats: current UUID-v4 ids and the one legacy
 * timestamp id shape created before server-owned UUID generation was enforced.
 */
const EVENT_ID_PATTERN: RegExp =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|evt-\d{13})$/;

/**
 * Returns true only for a supported stored event id. Rejecting every other
 * value keeps missing ids, sentinel strings, padded ids, and arbitrary values
 * from creating or matching orphan lock rows.
 */
export function isValidEditLockEventId(eventId: unknown): eventId is string {
  return typeof eventId === 'string' && EVENT_ID_PATTERN.test(eventId);
}

/** A held edit lock as returned by the backend. */
export interface EditLock {
  event_id: string;
  editor_alias: string;
  session_id: string;
  acquired_at: number;
  expires_at: number;
}

/** Narrows an unknown JSON value to a property-addressable object. */
function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Validates one lock entry independently so malformed entries are discarded. */
function isEditLock(value: unknown): value is EditLock {
  if (!isObjectRecord(value)) return false;
  return (
    isValidEditLockEventId(value.event_id) &&
    typeof value.editor_alias === 'string' &&
    typeof value.session_id === 'string' &&
    typeof value.acquired_at === 'number' &&
    typeof value.expires_at === 'number'
  );
}

/** Result of an acquire attempt. */
export interface AcquireLockResult {
  acquired: boolean;
  /** Alias of the current holder when the lock could not be acquired. */
  heldBy?: string;
}

/**
 * Attempts to acquire the edit lock for an event. Resolves with
 * `{ acquired: false }` (rather than throwing) when the request fails or the
 * lock is held by someone else, so callers can fall back to view mode.
 */
export async function acquireEditLock(eventId: string): Promise<AcquireLockResult> {
  if (!isValidEditLockEventId(eventId)) return { acquired: false };
  try {
    const response = await authedFetch(`/events/${encodeURIComponent(eventId)}/lock`, {
      method: 'POST',
      includeContentType: true,
      body: JSON.stringify({ editorAlias: getCurrentUserAlias(), sessionId: SESSION_ID }),
    });
    if (!response || !response.ok) return { acquired: false };
    return (await response.json()) as AcquireLockResult;
  } catch (err: unknown) {
    logger.error('acquireEditLock failed', {
      eventId,
      error: err instanceof Error ? err.message : 'Unknown error',
    });
    return { acquired: false };
  }
}

/**
 * Heartbeats the edit lock to extend its expiry while the modal stays open.
 * Best-effort: failures are logged, never thrown.
 */
export async function refreshEditLock(eventId: string): Promise<void> {
  if (!isValidEditLockEventId(eventId)) return;
  try {
    await authedFetch(`/events/${encodeURIComponent(eventId)}/lock`, {
      method: 'PUT',
      includeContentType: true,
      body: JSON.stringify({ sessionId: SESSION_ID }),
    });
  } catch (err: unknown) {
    logger.error('refreshEditLock failed', {
      eventId,
      error: err instanceof Error ? err.message : 'Unknown error',
    });
  }
}

/**
 * Releases the edit lock held by this session. Best-effort: failures are
 * logged, never thrown (the backend TTL releases abandoned locks regardless).
 */
export async function releaseEditLock(eventId: string): Promise<void> {
  if (!isValidEditLockEventId(eventId)) return;
  try {
    await authedFetch(`/events/${encodeURIComponent(eventId)}/lock`, {
      method: 'DELETE',
      includeContentType: true,
      body: JSON.stringify({ sessionId: SESSION_ID }),
    });
  } catch (err: unknown) {
    logger.error('releaseEditLock failed', {
      eventId,
      error: err instanceof Error ? err.message : 'Unknown error',
    });
  }
}

/**
 * Fetches every currently-held edit lock. Returns an empty array on failure so
 * a transient error never wrongly disables (or enables) Edit buttons.
 */
export async function fetchActiveLocks(): Promise<EditLock[]> {
  try {
    const response = await authedFetch('/locks');
    if (!response || !response.ok) return [];
    const data: unknown = await response.json();
    if (!isObjectRecord(data) || !Array.isArray(data.locks)) return [];
    return data.locks.filter(isEditLock);
  } catch (err: unknown) {
    logger.error('fetchActiveLocks failed', {
      error: err instanceof Error ? err.message : 'Unknown error',
    });
    return [];
  }
}

/**
 * True when an event is locked by a user other than the current one (i.e. its
 * Edit button should be disabled).
 */
export function isLockedByOther(lockOwner: string | undefined, currentUserAlias: string): boolean {
  return !!lockOwner && lockOwner !== currentUserAlias;
}
